import { Participant } from "./participant";
import { SessionTypes, ParticipantTypes } from "./types";

const VIDEO_FREEZE_THRESHOLD_MS = 20000;  // if video is frozen for this long, disconnect

export class VideoDeviceParticipant extends Participant {
  /**
   * 
   * @param {string} peerId - the id of the peer
   * @param {RTCPeerConnection} rtcPeerConnection - the RTCPeerConnection object 
   * @param {ConnectionContext} context - the connection context
   * @param {int} videoDeviceId - the id of the video device
   * @param {function} dataChannelMessageListener - listener for data channel messages
   * @param {boolean} sendVideo - whether to send video (for devices that support it)
   */
  constructor({ peerId, rtcPeerConnection, context, videoDeviceId, dataChannelMessageListener, sendVideo }) {
    super({ peerId, rtcPeerConnection, context, participantType: ParticipantTypes.device, dataChannelMessageListener });
    this.videoDeviceId = videoDeviceId;  // numeric API id of the video device
    this.videoFrozenAt = null;  // timestamp of when the video was frozen
    this.sendVideo = sendVideo;  // whether to send video (for devices that support it)
  }

  async connectLocalMedia() {
    const localMediaStream = this.context.localMediaStream;
    if (localMediaStream) {
      console.debug("local media stream available", localMediaStream);
      // the stream may have video but that may be separately sent to a kiosk connection
      localMediaStream.getAudioTracks().forEach((track) => {
        if (this.context.sessionType === SessionTypes.monitoring) {
          track.enabled = false;  // disable audio for monitoring
          console.debug(`disabling audio track ${track.kind} for monitoring`, track);
        }
        super.addLocalMediaTrack(track, localMediaStream);
      });
      if (this.sendVideo) {
        console.debug("connecting video tracks for video device", this.videoDeviceId);
        localMediaStream.getVideoTracks().forEach((track) => {
          super.addLocalMediaTrack(track, localMediaStream);
        });
      }
    } else if (this.context.sessionType !== SessionTypes.test) {
      console.error("no local media stream available");
      this.emit("error", "no-local-media-stream");
    }
  }

  configurePeerConnectionListeners() {
    super.configurePeerConnectionListeners();
    const peerConn = this.rtcPeerConnection;

    // special handling for video device (patient) connections
    peerConn.addEventListener("track", (event) => {
      event.track.onmute = (event) => {
        console.warn(`device ${this.videoDeviceId} video track for ${this.peerId} was muted/frozen`, event);
        this.videoFrozenAt = Date.now();
        setTimeout(() => {
          console.debug(`checking if video is still frozen... ${this.videoFrozenAt}`);
          if (this.videoFrozenAt && Date.now() - this.videoFrozenAt >= (VIDEO_FREEZE_THRESHOLD_MS - 250)) {
            console.warn(`device ${this.videoDeviceId} video track for ${this.peerId} is still frozen - disconnecting`);
            this.emit("error", "patient-video-frozen");
          } else if (this.videoFrozenAt) {
            console.info(`patient ${this.videoDeviceId} video track for ${this.peerId} is still frozen - assuming another check is coming`)
          } else {
            console.info(`patient ${this.videoDeviceId} video track for ${this.peerId} is no longer frozen`);
          }
        }, VIDEO_FREEZE_THRESHOLD_MS);
      }
      event.track.onunmute = (event) => {
        console.info(`patient ${this.videoDeviceId} video track for ${this.peerId} was unmuted`, event);
        this.videoFrozenAt = null;
      }
      // a remote track ending or leaving the stream is otherwise completely silent: no mute,
      // no connection state change. It does NOT fire on our own pc.close()/track.stop()
      // (the spec ends tracks silently there), so this only logs sender-side track death.
      event.track.onended = () => {
        console.error(`device ${this.videoDeviceId} ${event.track.kind} track (${event.track.id}) for ${this.peerId} ended`);
      }
      // property assignment (not addEventListener): this track handler runs once per track
      // (audio + video) with the same streams[0], so a listener would attach twice
      event.streams[0].onremovetrack = (removeEvent) => {
        console.error(`device ${this.videoDeviceId} ${removeEvent.track.kind} track (${removeEvent.track.id}) removed from stream for ${this.peerId}`);
      }
    })

    console.debug(`video device ${this.videoDeviceId} peer connection listeners configured`);
  }
}